/**
 * 面談・研修の記録の入力(D-197)。日付・種類・どの段階の面談か・担当・備考。
 * 2026-10-15 発注者指示: 課題(タグの複数選択。給湯器だけ)と、やったこと(タグ＋詳細のメモを行で足していく)
 * 削除は確認を出してから(レポートの本文は監査ログに残さない。監査ログは閲覧者も読めるため)。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import DateField from '@core/components/DateField';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { useToast } from '@core/components/Toast';
import TagPicker from '@core/components/ui/TagPicker';
import { BUSINESS_LABELS, type BusinessType, type DateStr } from '@core/lib/types';
import { todayStr } from '@core/lib/date';
import { ACTION_TAGS, ISSUE_TAGS, MILESTONES, RECORD_KIND_LABELS, type Milestone, type TrainingAction, type TrainingRecord, type TrainingRecordKind } from './training';
import { createTrainingRecord, deleteTrainingRecord, updateTrainingRecord, type TrainingRecordInput } from './trainingRepo';

/** 入力した行の数に合わせて高さを変える(改行のたびに伸び縮みする) */
const fitHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
};

export type RecordDraft = { opId: string; name: string; businesses: BusinessType[]; business: BusinessType; milestone: Milestone | null; record?: TrainingRecord };

export default function TrainingRecordModal({ draft, userId, userName, onClose, onSaved }: { draft: RecordDraft; userId: string; userName: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const r = draft.record;
  const initial: TrainingRecordInput = {
    op_id: draft.opId,
    business: r?.business ?? draft.business,
    date: r?.date ?? todayStr(),
    kind: r?.kind ?? 'interview',
    milestone: r ? r.milestone : draft.milestone,
    trainer: r?.trainer ?? userName,
    report: r?.report ?? '',
    issues: r?.issues ?? [],
    // 新しい記録は最初から1行出しておく
    actions: r?.actions?.length ? r.actions : r ? [] : [{ tag: '', memo: '' }],
  };
  const [form, setForm] = useState<TrainingRecordInput>(initial);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const guard = useUnsavedGuard(dirty);
  /** 入力途中で閉じるときは確認する(レポートの書きかけを失わないように) */
  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());
  const set = (patch: Partial<TrainingRecordInput>) => setForm((f) => ({ ...f, ...patch }));
  const setAction = (i: number, patch: Partial<TrainingAction>) => set({ actions: form.actions.map((a, k) => (k === i ? { ...a, ...patch } : a)) });
  /** 課題は給湯器だけ(2026-10-15 発注者指示) */
  const showIssues = form.business === 'kyutoki';

  async function save() {
    if (!form.trainer.trim()) {
      toast('担当を入れてください');
      return;
    }
    const actions = form.actions.map((a) => ({ tag: a.tag, memo: a.memo.trim() })).filter((a) => a.tag || a.memo);
    if (actions.some((a) => !a.tag)) {
      toast('やったことのタグを選んでください(詳細だけの行があります)');
      return;
    }
    setBusy(true);
    try {
      const input = { ...form, trainer: form.trainer.trim(), report: form.report.trim(), issues: showIssues ? form.issues : [], actions };
      if (r) await updateTrainingRecord(r.id, input, userId);
      else await createTrainingRecord(input, userId);
      toast(`${RECORD_KIND_LABELS[form.kind]}の記録を保存しました`);
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? `保存できませんでした: ${e.message}` : '保存できませんでした');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!r) return;
    setBusy(true);
    try {
      await deleteTrainingRecord(r, userId);
      toast('記録を削除しました');
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? `削除できませんでした: ${e.message}` : '削除できませんでした');
      setBusy(false);
    }
  }

  return (
    <Modal onClose={requestClose} width="min(640px, 94vw)">
      <h3 className="training-modal-title">
        {draft.name} の{r ? '記録を編集' : '面談・研修・指導を記録'}
      </h3>
      <div className="training-form">
        <label className="training-field">
          <span className="ui-hint">日付</span>
          <DateField variant="ui" value={form.date} onChange={(d: DateStr) => set({ date: d })} />
        </label>
        <label className="training-field">
          <span className="ui-hint">種類</span>
          <select className="ui-input" value={form.kind} onChange={(e) => set({ kind: e.target.value as TrainingRecordKind })}>
            {(Object.keys(RECORD_KIND_LABELS) as TrainingRecordKind[]).map((k) => (
              <option key={k} value={k}>
                {RECORD_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        <label className="training-field">
          <span className="ui-hint">カテゴリ</span>
          <select className="ui-input" value={form.business} onChange={(e) => set({ business: e.target.value as BusinessType })} disabled={draft.businesses.length < 2}>
            {draft.businesses.map((b) => (
              <option key={b} value={b}>
                {BUSINESS_LABELS[b]}
              </option>
            ))}
          </select>
        </label>
        <label className="training-field">
          <span className="ui-hint">段階</span>
          <select className="ui-input" value={form.milestone ?? ''} onChange={(e) => set({ milestone: e.target.value ? (Number(e.target.value) as Milestone) : null })}>
            <option value="">段階と関係ない</option>
            {MILESTONES.map((m) => (
              <option key={m} value={m}>
                {m}h の面談
              </option>
            ))}
          </select>
        </label>
        <label className="training-field">
          <span className="ui-hint">担当</span>
          <input className="ui-input" value={form.trainer} onChange={(e) => set({ trainer: e.target.value })} maxLength={40} />
        </label>
        {showIssues && (
          <div className="training-field is-wide">
            <span className="ui-hint">課題(いくつでも)</span>
            <TagPicker label="課題" options={ISSUE_TAGS} value={form.issues} onChange={(issues) => set({ issues })} />
          </div>
        )}
        <div className="training-field is-wide">
          <span className="ui-hint">やったこと</span>
          {form.actions.map((a, i) => (
            <div key={i} className="training-action-row">
              <select className="ui-input" aria-label={`やったこと${i + 1}のタグ`} value={a.tag} onChange={(e) => setAction(i, { tag: e.target.value })}>
                <option value="">タグを選ぶ</option>
                {ACTION_TAGS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {/* 詳細は改行でき、行が増えると高さも伸びる(2026-10-15 発注者指示) */}
              <textarea
                className="ui-input training-action-memo"
                aria-label={`やったこと${i + 1}の詳細`}
                placeholder="詳細(メモ)"
                rows={1}
                ref={fitHeight}
                value={a.memo}
                onChange={(e) => {
                  fitHeight(e.target);
                  setAction(i, { memo: e.target.value });
                }}
                maxLength={1000}
              />
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" aria-label={`やったこと${i + 1}を消す`} onClick={() => set({ actions: form.actions.filter((_, k) => k !== i) })}>
                消す
              </button>
            </div>
          ))}
          <div>
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => set({ actions: [...form.actions, { tag: '', memo: '' }] })}>
              ＋ やったことを追加
            </button>
          </div>
        </div>
        <label className="training-field is-wide">
          <span className="ui-hint">備考</span>
          <textarea className="ui-input" rows={5} value={form.report} onChange={(e) => set({ report: e.target.value })} maxLength={4000} />
        </label>
      </div>
      <p className="ui-hint">段階を選ぶと、その段階の面談のアラートが消えます。</p>
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" onClick={() => void save()} disabled={busy || !dirty}>
          {busy ? '保存中…' : '保存する'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={requestClose} disabled={busy}>
          やめる
        </button>
        {r && (
          <button type="button" className="ui-btn ui-btn-secondary training-danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
            削除
          </button>
        )}
      </div>

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(false)} width="min(440px, 94vw)" showClose={false}>
          <p>
            {r?.date} の{r ? RECORD_KIND_LABELS[r.kind] : ''}の記録を削除します。削除したレポートは元に戻せません。よろしいですか?
          </p>
          <div className="training-actions">
            <button type="button" className="ui-btn ui-btn-secondary training-danger" onClick={() => void remove()} disabled={busy}>
              削除する
            </button>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setConfirmDelete(false)} disabled={busy}>
              やめる
            </button>
          </div>
        </Modal>
      )}
      {confirmClose && (
        <Modal onClose={() => setConfirmClose(false)} width="min(440px, 94vw)" showClose={false}>
          <p>入力した内容は保存されていません。閉じてよろしいですか?</p>
          <div className="training-actions">
            <button type="button" className="ui-btn ui-btn-primary" onClick={() => setConfirmClose(false)}>
              入力に戻る
            </button>
            <button type="button" className="ui-btn ui-btn-secondary training-danger" onClick={onClose}>
              保存せずに閉じる
            </button>
          </div>
        </Modal>
      )}
      {guard.modal}
    </Modal>
  );
}
